'use client';

import React from 'react';
import {
  CheckCircleIcon,
  ClockCounterClockwiseIcon,
  HourglassIcon,
  ListBulletsIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react';
import { SegmentedControl } from '@tranquility-ai/theme-components/segmented-control';
import { StatusPill } from '@tranquility-ai/theme-components/status-pill';
import { useTimePilotT } from '@/i18n/useTimePilotT';

import './csam-report-nav-bar.css';

export type CsamReportTab = 'list' | 'review' | 'audit';

export interface CsamReportNavBarProps {
  caseName: string;
  reviewer?: string;
  activeTab: CsamReportTab;
  onActiveTabChange: (tab: CsamReportTab) => void;
  /** When true, all findings are reviewed (no queue left). */
  isFullyReviewed: boolean;
  embeddedInAppShell?: boolean;
  showReviewTab?: boolean;
  showAuditTab?: boolean;
  /**
   * When false, All Cases is selected. Hide the review-status pill and the
   * List / Review / Audit switcher until a concrete case is selected.
   */
  hasSelectedCase?: boolean;
}

export function CsamReportNavBar({
  caseName,
  reviewer,
  activeTab,
  onActiveTabChange,
  isFullyReviewed,
  embeddedInAppShell = true,
  showReviewTab = true,
  showAuditTab = true,
  hasSelectedCase = true,
}: CsamReportNavBarProps) {
  const t = useTimePilotT();
  return (
    <nav
      id="csam-report-nav-bar"
      role="navigation"
      aria-label={t('csam.nav.aria')}
      className={
        embeddedInAppShell
          ? 'tp-secondary-nav tp-secondary-nav--docked tp-csam-report-nav--docked'
          : 'tp-secondary-nav tp-secondary-nav--docked'
      }
    >
      <div className="tp-secondary-nav__start tp-secondary-nav__start--column min-w-0 flex-1">
        <div className="flex min-w-0 flex-row items-center gap-2">
          <span className="truncate text-2xl font-bold text-white">{t('workProducts.csamDashboard')}</span>
          {hasSelectedCase &&
            (isFullyReviewed ? (
              <StatusPill
                size="sm"
                inverse
                color="success"
                leadingIcon={<CheckCircleIcon size={13} weight="fill" />}
              >
                {t('csam.badge.reviewed')}
              </StatusPill>
            ) : (
              <StatusPill
                size="sm"
                inverse
                color="warning"
                leadingIcon={<HourglassIcon size={13} weight="fill" />}
              >
                {t('csam.badge.underReview')}
              </StatusPill>
            ))}
        </div>
        <span className="truncate text-sm italic text-white/65">
          {t('csam.caseLine', { caseName, reviewer: reviewer || '—' })}
        </span>
      </div>

      {hasSelectedCase && (
        <div className="tp-secondary-nav__end">
          <SegmentedControl
            aria-label={t('csam.nav.viewAria')}
            size="lg"
            variant="dark"
            className="tp-work-product-subnav__segmented"
            value={activeTab}
            onValueChange={(value) => onActiveTabChange(value as CsamReportTab)}
            options={[
              { value: 'list', label: t('csam.tab.list'), icon: <ListBulletsIcon size={16} weight="bold" /> },
              ...(showReviewTab
                ? [{ value: 'review', label: t('csam.tab.review'), icon: <MagnifyingGlassIcon size={16} weight="bold" /> }]
                : []),
              ...(showAuditTab
                ? [{ value: 'audit', label: t('csam.tab.audit'), icon: <ClockCounterClockwiseIcon size={16} weight="bold" /> }]
                : []),
            ]}
          />
        </div>
      )}
    </nav>
  );
}
